import { SaveOperation } from '@infuse/api/common';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericDeactivateUser = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<void, unknown>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; id: string }>({
    mutationFn: async ({ parentId, id }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn(parentId ? { id: parentId, userId: id } : { userId: id }, undefined);
        notify({
          title: t('iam.users.header.deactivate_user'),
          message: t('iam.users.notify.deactivate_user_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.users.header.deactivate_user'),
          message: getAxiosErrorMessage(e, t('iam.users.notify.deactivate_user_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  return {
    ...mutation
  };
};
